import { createGetSuggestionsFromApiUrl } from '../crm/shared/field_inputs/HelpMethods';
import EditText from '../crm/shared/field_inputs/text/EditText';
import EditDateTime from '../crm/shared/field_inputs/date/EditDateTime';
import React, { useContext, useMemo } from 'react';
import { navigate } from 'hookrouter';
import { DataContext as SharedDataContext } from '../../stores/crm-store';
import { HandlePatchLecture } from './requests/LectureRequests';
import { GetTeachersByIds } from './requests/TeacherRequests';
import ExlEditItemList from './field_inputs/item/ExlEditItemList';
import { allowedRoleFormComponents } from './CourseHelperFunctions';
import ExlEditDraft from './field_inputs/ExlEditDraft';
import ExlFormModal from './shared/ExlFormModal';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { EDIT_LECTURE_LABEL, EMAIL_NOTIFICATIONS_FALSE, EMAIL_NOTIFICATIONS_TRUE, LECTURE_MODALS_DESC_LABEL, LECTURE_MODALS_INVALID_TIME_VALI, LECTURE_MODALS_MEETING_LINK_LABEL, LECTURE_MODALS_NAME_LABEL, LECTURE_MODALS_NO_NAME_VALI, LECTURE_MODALS_NO_TEACHERS_VALI, LECTURE_MODALS_TEACHERS_LABEL } from '../../i18n/keyConstants';

const EditLecture = ({ updateLecture, submitHandler, permissionRole, open, onClose }) => {
    const { dispatch: sharedDispatch } = useContext(SharedDataContext);

    const oldLecture = updateLecture;
    const { t } = useTranslation(['lectureModals', 'editLecture', 'shared']);
    const defaultFormValues = useMemo(() => ({
        name: updateLecture.name,
        teachers: updateLecture.teachers,
        description: updateLecture.description,
        timeRange: { from: moment.utc(updateLecture.startTime).local(), to: moment.utc(updateLecture.endTime).local() },
        meetingLink: updateLecture.meetingLink
    }), [updateLecture]);

    const formComponents = [
        {
            propName: 'name',
            label: t(LECTURE_MODALS_NAME_LABEL),
            validate: (v) => (!v || !v.length ? t(LECTURE_MODALS_NO_NAME_VALI) : ''),
            component: <EditText />,
        },
        {
            propName: 'description',
            label: t(LECTURE_MODALS_DESC_LABEL),
            component: <ExlEditDraft/>
        },
        {
            propName: 'teachers',
            label: t(LECTURE_MODALS_TEACHERS_LABEL),
            validate: (v) => (!v || v.length < 1 ? t(LECTURE_MODALS_NO_TEACHERS_VALI) : ''),
            component:
                <ExlEditItemList
                    iconName='teacher'
                    isClearable
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/exlTeacherFront/getTeacherSuggestions')}/>
        },
        {
            propName: 'timeRange',
            label: '',
            validate: (v) => (!v ? t(LECTURE_MODALS_INVALID_TIME_VALI) : ''),
            component: <EditDateTime />,
        },
        {
            propName: 'meetingLink',
            label: t(LECTURE_MODALS_MEETING_LINK_LABEL),
            component: <EditText />,
        },
    ];

    const filterFormComponents = () =>
        formComponents.filter((component) => {
            const isAllowed = allowedRoleFormComponents(permissionRole, formComponents).includes(component.propName);
            return isAllowed;
        });

    const buildPatchedLectureObject = async (lecture) => {
        const populatedLecture = ({
            ...lecture,
            id: oldLecture.id,
            courseId: oldLecture.courseId,
            endTime: moment(lecture.timeRange.to).utc(),
            startTime: moment(lecture.timeRange.from).utc()
        });
        //deletes timeRange prop from lecture object
        const { timeRange, ...rest } = populatedLecture;
        return rest;
    }

    const handleAdd = async (lecture) => {
        const teacherIds = lecture.teachers.map((teacher) => teacher.id);
        lecture.teachers = await GetTeachersByIds(teacherIds);
        
        const populatedLecture = await buildPatchedLectureObject(lecture);
        if (permissionRole === "teacher") {
            populatedLecture.startTime = moment(populatedLecture.startTime).format('YYYY-MM-DD HH:mm:ss');
            populatedLecture.endTime = moment(populatedLecture.endTime).format('YYYY-MM-DD HH:mm:ss');
        }

        const response = await HandlePatchLecture(oldLecture, populatedLecture)
        if (response.success) {
            sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
            submitHandler();
            navigate('/exlLectureDetails/' + response.data.id);
        }
        else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }
        return response;
    };

    return (
        <ExlFormModal
            open={open}
            onClose={onClose}
            description={t(EDIT_LECTURE_LABEL, {ns: 'editLecture'})}
            defaultFormValues={defaultFormValues}
            defaultFormComponents={formComponents}
            handleAdd={handleAdd}
            onSuccessiveSave={() => submitHandler()}
            callback={filterFormComponents}
            isExl
            iconBool={updateLecture.course.sendEmailNotifications}
            trueText={t(EMAIL_NOTIFICATIONS_TRUE, {ns: 'shared'})}
            falseText={t(EMAIL_NOTIFICATIONS_FALSE, {ns: 'shared'})}
        />
    );
}
export default EditLecture;


